import type { ColumnDef } from '@tanstack/vue-table';
import { h } from 'vue';

import type { SMSTemplateSchema } from '@stuut/api-sdk/models';
import { Badge } from '@ui/components/badge';
import { Checkbox } from '@ui/components/checkbox';
import { DataTableColumnHeader } from '@ui/components/datatable';

// Format Jinja variables for display: {{ variable_name }} -> [variable_name]
const formatJinjaVariables = (text: string): string => {
  return text.replace(/\{\{\s*(\w+)\s*\}\}/g, '[$1]');
};

export const SmsTemplateColumns: ColumnDef<SMSTemplateSchema>[] = [
  {
    cell: ({ row }) => {
      return h(
        'div',
        { class: 'flex flex-row justify-center' },
        h(Checkbox, {
          ariaLabel: 'Select row',
          modelValue: row.getIsSelected(),
          'onUpdate:modelValue': (value: any) => row.toggleSelected(Boolean(value)),
        }),
      );
    },
    enableHiding: false,
    enableSorting: false,
    header: ({ table }) =>
      h(
        'div',
        { class: 'flex flex-row justify-center' },
        h(Checkbox, {
          ariaLabel: 'Select all',
          indeterminate: table.getIsSomePageRowsSelected(),
          modelValue: table.getIsAllPageRowsSelected(),
          'onUpdate:modelValue': (value: any) => table.toggleAllPageRowsSelected(Boolean(value)),
        }),
      ),
    id: 'select',
    size: 40,
  },
  {
    accessorKey: 'name',
    cell: ({ row }) => {
      return h('div', { class: 'flex items-center gap-2' }, [
        h(
          'span',
          { class: 'text-primary hover:underline cursor-pointer' },
          row.original.name || 'New SMS Template',
        ),
        row.original.isDefault &&
          h(
            Badge,
            {
              size: 'sm',
              variant: 'open',
            },
            () => 'Default',
          ),
      ]);
    },
    enableSorting: true,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Name' }),
    id: 'name',
    size: 250,
  },
  {
    accessorKey: 'body',
    cell: ({ row }) => {
      const body = row.original.body;
      return body
        ? h('span', { class: 'truncate' }, formatJinjaVariables(body))
        : h('span', { class: 'text-muted-foreground' }, 'No Body');
    },
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Body' }),
    id: 'body',
    meta: {
      class: 'truncate',
    },
    size: 400,
  },
  {
    accessorKey: 'characterCount',
    cell: ({ row }) => {
      const count = row.original.characterCount;
      return h('span', { class: 'text-muted-foreground text-xs' }, `${count} chars`);
    },
    enableSorting: false,
    header: ({ column }) => h(DataTableColumnHeader, { column, title: 'Length' }),
    id: 'characterCount',
    size: 100,
  },
];
